<script>
import { mapActions } from 'vuex';
import UploadImage from '@/components/ux/upload/Upload-image.vue';

export default {
  name: 'EditTypesListModal',
  components: { UploadImage },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['save', 'cancel'],
  data() {
    return {
      localItems: [],
      isSaving: false,
      uploadKey: 0,
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initializeData();
      }
    },
  },
  methods: {
    ...mapActions({
      uploadModuleImagesAction: 'pageContent/uploadModuleImages',
    }),
    initializeData() {
      this.uploadKey += 1;
      const source = this.data && this.data.length > 0 ? this.data : [this.createEmptyItem()];
      this.localItems = JSON.parse(JSON.stringify(source)).map((item) => ({
        ...item,
        pendingDesktopFile: null,
        pendingMobileFile: null,
      }));
    },
    createEmptyItem() {
      return {
        id: Date.now(),
        title: '',
        description: '',
        descriptionMob: '',
        image: '',
        imageMob: '',
        link: '',
      };
    },
    addItem() {
      this.localItems.push({
        ...this.createEmptyItem(),
        id: Date.now() + this.localItems.length,
        pendingDesktopFile: null,
        pendingMobileFile: null,
      });
    },
    removeItem(index) {
      if (this.localItems.length > 1) {
        this.localItems.splice(index, 1);
      }
    },
    updateItem(index, field, value) {
      this.localItems[index][field] = value;
    },
    updateItemFile(index, type, files) {
      if (type === 'desktop') {
        this.localItems[index].pendingDesktopFile = files[0] || null;
      } else {
        this.localItems[index].pendingMobileFile = files[0] || null;
      }
    },
    async handleSave() {
      this.isSaving = true;
      try {
        const uploadPromises = this.localItems.map((item) => this.uploadModuleImagesAction({
          desktopFile: item.pendingDesktopFile || null,
          mobileFile: item.pendingMobileFile || null,
          currentImage: item.image,
          currentImageMobile: item.imageMob,
        }));
        const uploadResults = await Promise.all(uploadPromises);
        const result = this.localItems.map((item, idx) => ({
          id: item.id,
          title: item.title,
          description: item.description,
          descriptionMob: item.descriptionMob,
          link: item.link || '',
          image: uploadResults[idx].image,
          imageMob: uploadResults[idx].imageMobile,
        }));
        this.$emit('save', result);
      } finally {
        this.isSaving = false;
      }
    },
    handleCancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<template>
  <a-modal
    :open="visible"
    title="Edit Types List"
    :width="800"
    :z-index="2000"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="types-editor">
      <div class="items-list">
        <div
          v-for="(item, index) in localItems"
          :key="index"
          class="item-block"
        >
          <div class="item-header">
            <span class="item-number">Card {{ index + 1 }}</span>
            <a-button
              v-if="localItems.length > 1"
              size="small"
              danger
              @click="removeItem(index)"
            >
              Remove
            </a-button>
          </div>

          <div class="item-fields">
            <div class="field-group">
              <span class="field-label">Title</span>
              <a-input
                :value="item.title"
                placeholder="Enter title"
                @input="e => updateItem(index, 'title', e.target.value)"
              />
            </div>

            <div class="field-group">
              <span class="field-label">Description (Desktop)</span>
              <a-textarea
                :value="item.description"
                placeholder="Enter description for desktop"
                :rows="3"
                @input="e => updateItem(index, 'description', e.target.value)"
              />
            </div>

            <div class="field-group">
              <span class="field-label">Description (Mobile)</span>
              <a-textarea
                :value="item.descriptionMob"
                placeholder="Enter description for mobile"
                :rows="3"
                @input="e => updateItem(index, 'descriptionMob', e.target.value)"
              />
            </div>

            <div class="field-group">
              <span class="field-label">Link (URL)</span>
              <a-input
                :value="item.link"
                placeholder="e.g. /vhs or https://..."
                @input="e => updateItem(index, 'link', e.target.value)"
              />
            </div>

            <div class="images-row">
              <div class="field-group">
                <span class="field-label">Desktop Image</span>
                <UploadImage
                  :key="`desktop-${index}-${uploadKey}`"
                  :is-multiple="false"
                  :initial-images="item.image ? [item.image] : []"
                  @update:files="files => updateItemFile(index, 'desktop', files)"
                />
              </div>
              <div class="field-group">
                <span class="field-label">Mobile Image</span>
                <UploadImage
                  :key="`mobile-${index}-${uploadKey}`"
                  :is-multiple="false"
                  :initial-images="item.imageMob ? [item.imageMob] : []"
                  @update:files="files => updateItemFile(index, 'mobile', files)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <a-button
        type="dashed"
        class="add-button"
        @click="addItem"
      >
        + Add card
      </a-button>

      <div class="modal-footer">
        <a-button @click="handleCancel">
          Cancel
        </a-button>
        <a-button type="primary" :loading="isSaving" @click="handleSave">
          Save
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
.types-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 4px;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.item-block {
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.item-number {
  font-weight: 600;
  color: #262626;
}

.item-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .field-label {
    font-weight: 500;
    color: #595959;
    font-size: 13px;
  }
}

.images-row {
  display: flex;
  gap: 24px;

  .field-group {
    flex: 1;
  }
}

.add-button {
  width: fit-content;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
  margin-top: 8px;
}
</style>
